// Generic entity detail modal — opens on double-click or (i) icon click.
// Shows the 2D sprite large, all entity properties, and actions.

import type { SceneNode } from '@aod/protocol';
import { Badge } from '@aod/ui/components/ui/badge';
import { Button } from '@aod/ui/components/ui/button';
import { Progress } from '@aod/ui/components/ui/progress';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { SceneDialog } from '@aod/ui/components/ui/scene-dialog';
import { Anchor, Building2, Crown, Heart, Info, Shield, Swords, Users, X } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { isNeutralProps } from '../../ownership.js';
import { cityTexturePath, unitTexturePath } from '../../render/spriteMapping.js';
import { houseName } from '../../sea/houseNames.js';
import { merchantHouseOf } from '../../sea/merchantModel.js';
import { biomeLabel } from '../InfoPanel/biome.js';
import { heroPortraitId, sceneUrls } from '../scenes.js';

export interface EntityModalProps {
  readonly node: SceneNode | null;
  readonly localPlayerIndex: number;
  readonly matchOver: boolean;
  readonly onClose: () => void;
  readonly onAttack?: (cityId: string) => void;
  readonly onDeploy?: (cityId: string) => void;
}

export function EntityModal({
  node,
  localPlayerIndex,
  matchOver,
  onClose,
  onAttack,
  onDeploy,
}: EntityModalProps) {
  const t = useT();
  if (!node) return null;

  const isUnit = node.type === 'unit';
  const isCity = node.type === 'city';
  const isTile = node.type === 'tile';

  const neutral = isCity && isNeutralProps(node.props);
  const ownerId = (node.props?.ownerId as number | undefined) ?? 0;
  const isOwn = !neutral && ownerId === localPlayerIndex;
  // A foreign house's vessel: no player owns it, so it is named for its house.
  const house = merchantHouseOf(ownerId, node.props?.seaHouse as number | undefined);
  const isHostile = !isOwn && !neutral && ownerId !== undefined && ownerId !== 0 && !isTile;
  const factionColor = ownerId === 1 ? 'orange' : 'blue';

  const title = (() => {
    if (isUnit) {
      const hero = node.props?.hero as Record<string, unknown> | undefined;
      return (hero?.name as string) ?? node.name ?? t('selection.unit');
    }
    if (isCity) return node.name ?? t('selection.city');
    if (isTile) return biomeLabel(t, node.props?.biome as string | undefined);
    return t('entity.entity');
  })();

  const spriteUrl = (() => {
    if (isUnit) {
      const kind = (node.props?.kind as string) ?? 'warrior';
      return unitTexturePath(kind, ownerId);
    }
    if (isCity) {
      const tier = (node.props?.tier as number) ?? 1;
      return cityTexturePath(tier, ownerId);
    }
    return null;
  })();

  const heroProps = node.props?.hero as Record<string, unknown> | undefined;
  const portrait = heroProps
    ? heroPortraitId(String(heroProps.archetype ?? ''), heroProps.female === true)
    : null;

  return (
    <SceneDialog
      onClose={onClose}
      title={title}
      description={
        <span className="font-mono text-[11px]">
          {isUnit ? t('selection.unit') : isCity ? t('selection.city') : t('selection.tile')}
          {node.props?.q !== undefined && (
            <>
              {' '}
              · q={node.props.q} r={node.props.r}
            </>
          )}
        </span>
      }
      icon={
        spriteUrl && (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-lg border border-border/40 bg-muted">
            <img
              src={spriteUrl}
              alt={title}
              className="max-h-14 max-w-14 object-contain"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>
        )
      }
      media={
        portrait && (
          <SceneImage
            {...sceneUrls('heroPortrait', portrait)}
            widths={[256, 512]}
            sizes="(min-width: 640px) 448px, 90vw"
            aspect="strip"
            focus="top"
            alt={title}
          />
        )
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {/* Type badge + faction */}
        <div className="flex flex-wrap gap-1.5">
          {isUnit && (
            <>
              <Badge variant="secondary" className="font-mono text-[11px]">
                {(node.props?.kind as string) ?? 'warrior'}
              </Badge>
              {house !== null ? (
                // A foreign house's vessel belongs to no player: it wears the
                // house's colours and is named for the house.
                <Badge variant="outline" className="text-[11px]">
                  <Anchor className="mr-1 size-3" /> {t(houseName(house))}
                </Badge>
              ) : (
                ownerId !== 0 && (
                  <Badge variant="outline" className="text-[11px]">
                    <Crown className="mr-1 size-3" /> {t('common.player', { player: ownerId + 1 })}
                  </Badge>
                )
              )}
            </>
          )}
          {isCity && (
            <>
              <Badge variant="secondary" className="font-mono text-[11px]">
                T{(node.props?.tier as number) ?? 1}
              </Badge>
              <Badge variant="outline" className="text-[11px]">
                <Crown className="mr-1 size-3" />{' '}
                {neutral ? t('city.neutral') : t('common.player', { player: ownerId + 1 })}
              </Badge>
            </>
          )}
          {isTile && (
            <Badge variant="secondary" className="text-[11px]">
              {biomeLabel(t, node.props?.biome as string | undefined)}
            </Badge>
          )}
        </div>

        {/* HP */}
        {isUnit && node.props?.hpMax !== undefined && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Heart className="size-3" /> {t('common.hp')}
              </span>
              <span className="tabular-nums">
                {node.props.hp as number} / {node.props.hpMax as number}
              </span>
            </div>
            <Progress
              value={
                Number(node.props.hpMax) > 0
                  ? Math.round((Number(node.props.hp) / Number(node.props.hpMax)) * 100)
                  : 0
              }
            />
          </div>
        )}

        {/* City stats */}
        {isCity && (
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Users className="size-3" />
              {t('entity.population', {
                count: (node.props?.population as number)?.toLocaleString() ?? '0',
              })}
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Building2 className="size-3" />
              {t('entity.tier', { tier: (node.props?.tier as number) ?? 1 })}
            </div>
          </div>
        )}

        {/* Hero details */}
        {isUnit && node.tags?.includes?.('hero') && (
          <HeroDetailSection hero={node.props?.hero as Record<string, unknown> | undefined} />
        )}

        {/* Tile details */}
        {isTile && (
          <div className="text-[11px] text-muted-foreground">
            <p>
              {t('entity.biome', {
                biome: biomeLabel(t, node.props?.biome as string | undefined),
              })}
            </p>
            {node.props?.resource && (
              <p>{t('entity.resource', { resource: String(node.props.resource) })}</p>
            )}
          </div>
        )}

        {/* Actions */}
        {!matchOver && (
          <div className="flex gap-2 pt-2">
            {/* Only a city can be attacked from here: `onAttack` is the
                  city-attack intent. A unit is fought by moving onto it. */}
            {isHostile && isCity && onAttack && (
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => onAttack(node.id)}
              >
                <Swords className="mr-1 size-4" /> {t('entity.attack')}
              </Button>
            )}
            {isOwn && isCity && onDeploy && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onDeploy(node.id)}
              >
                <Shield className="mr-1 size-4" /> {t('entity.deploy')}
              </Button>
            )}
          </div>
        )}
      </div>
    </SceneDialog>
  );
}

function HeroDetailSection({ hero }: { hero: Record<string, unknown> | undefined }) {
  const t = useT();
  if (!hero) return null;
  return (
    <div className="space-y-1.5 rounded-md border border-border/40 bg-muted/20 p-2.5">
      <p className="text-[11px] font-semibold text-foreground/80">
        {t('entity.heroLevel', {
          archetype: String(hero.archetype ?? t('unitTray.hero')),
          level: String(hero.level ?? 1),
        })}
      </p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span>{t('entity.role', { role: String(hero.role ?? '—') })}</span>
        <span>{t('entity.xp', { xp: String(hero.xp ?? 0) })}</span>
        <span>{t('entity.loyalty', { loyalty: String(hero.loyalty ?? 50) })}</span>
        {(hero.isWounded as boolean) && (
          <span className="text-red-700 dark:text-red-400">{t('hero.wounded')}</span>
        )}
        {(hero.isCaptured as boolean) && (
          <span className="text-red-700 dark:text-red-400">{t('hero.captured')}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Small (i) info button to trigger the modal. Renders at the top-right
 * corner of an entity in the side panel.
 */
export function InfoButton({ onClick, label }: { onClick: () => void; label?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      className="ml-auto inline-flex size-5 items-center justify-center rounded-full bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label ?? t('entity.viewDetails')}
      title={label ?? t('entity.viewDetails')}
    >
      <Info className="size-3" />
    </button>
  );
}
